import { type FunctionComponent, useState } from 'react';
import { ClickableElement, StyledButton } from '../../../../../components/buttons';
import { Carousel } from '../../../../../components/carousel/carousel.component';
import { CloudinaryImage } from '../../../../../components/images/cloudinary-image/cloudinary-image.component';
import { styleTags } from '../../../../../constants/project/shop-by-look';
import type { StyleTag } from '../../../../../types/project.types';

export type StylesFilterButtonProps = {
	style: StyleTag;
	isSelected: boolean;
	onClick: (style: string) => void;
};

export const StylesFilterButton: FunctionComponent<StylesFilterButtonProps> = ({ style, isSelected = false, onClick }) => (
	<ClickableElement ariaLabel={`${style.value} style filter`} className="relative f6" onClick={() => onClick(style.value.toLowerCase())}>
		{/* if you conditionally render the border, the image jumps on selection; setting it white first prevents this */}
		<div className={`${isSelected ? 'b--theme-grey' : 'b--theme-white'} ba bw1 br0 pa2 ma0`} data-testid="button-container">
			<div className="flex flex-column" style={{ minWidth: 293 }}>
				<CloudinaryImage
					options={{ crop: 'fill', width: 293, height: 195 }}
					publicID={style.image}
					description={`A photo of a ${style.value} style`}
					className="br3"
				/>
				<span className="pt1">{style.value}</span>
			</div>
		</div>
	</ClickableElement>
);

export type StylesFilterButtonContainerProps = {
	selectedStyles: string[];
	onSelectedStyle: (style: string) => void;
	className: string;
	startIndex?: number;
	endIndex?: number;
};

export const StylesFilterButtonContainer: FunctionComponent<StylesFilterButtonContainerProps> = ({
	selectedStyles,
	onSelectedStyle,
	className,
	startIndex,
	endIndex
}) => {
	const slicedStyleTags = styleTags.slice(startIndex, endIndex);
	return (
		<div className={`flex ${className}`}>
			{slicedStyleTags.map((style) => (
				<StylesFilterButton
					key={style.value}
					style={style}
					isSelected={selectedStyles.includes(style.value.toLowerCase())}
					onClick={onSelectedStyle}
				/>
			))}
		</div>
	);
};

export type FilterByStylesProps = {
	selectedStyles: string[];
	onSelectedStyle: (style: string) => void;
	isAllFavorites?: boolean;
};

export const FilterByStyles: FunctionComponent<FilterByStylesProps> = ({ onSelectedStyle, selectedStyles, isAllFavorites }) => {
	const [isStylesMenuOpen, setIsStylesMenuOpen] = useState<boolean>(true);

	const toggleStylesMenu = () => {
		setIsStylesMenuOpen(!isStylesMenuOpen);
	};

	return (
		<div className="flex flex-column items-center w-100">
			<div className={`flex flex-column w-100 w-80-l ${isAllFavorites ? '' : 'bb b--theme-grey-light'}`}>
				<div className="flex flex-column items-center mv3">
					<StyledButton buttonStyle="SECONDARY" ariaLabel="rooms menu toggle" onClick={toggleStylesMenu} size="SMALL">
						{isStylesMenuOpen ? <span>Hide Styles</span> : <span>Show Styles</span>}
					</StyledButton>
				</div>
				{isStylesMenuOpen && (
					<Carousel>
						{/* Desktop */}
						<div className="dn flex-l pa3" data-testid="styles-container">
							<div className="flex flex-column w-100">
								<StylesFilterButtonContainer
									className="mb3 gr5 gc5"
									startIndex={0}
									endIndex={3}
									selectedStyles={selectedStyles}
									onSelectedStyle={onSelectedStyle}
								/>
								<StylesFilterButtonContainer
									className="mt3 gc5"
									startIndex={3}
									endIndex={6}
									selectedStyles={selectedStyles}
									onSelectedStyle={onSelectedStyle}
								/>
							</div>
							<div className="flex flex-column ml5 mb5 w-100">
								<StylesFilterButtonContainer
									className="mb3 gr5 gc5"
									startIndex={6}
									endIndex={9}
									selectedStyles={selectedStyles}
									onSelectedStyle={onSelectedStyle}
								/>
								<StylesFilterButtonContainer
									className="mt3 gr5 gc5"
									startIndex={9}
									selectedStyles={selectedStyles}
									onSelectedStyle={onSelectedStyle}
								/>
							</div>
						</div>
						{/* Mobile */}
						<div className="flex dn-l pb5 gc4">
							{styleTags.map((tag) => {
								return (
									<StylesFilterButton
										key={tag.value}
										style={tag}
										isSelected={selectedStyles.includes(tag.value.toLowerCase())}
										onClick={onSelectedStyle}
									/>
								);
							})}
						</div>
					</Carousel>
				)}
			</div>
		</div>
	);
};
